import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSplit } from '@fortawesome/pro-regular-svg-icons/faSplit';

import React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';

import { ComponentStates } from '../../../../../../enums/component-states';
import { useEntitiesFulfillmentPlansFromSalesOrderId } from '../../../../../../hooks/entities/selectors/fulfillment-plans';
import { useSalesOrderRestrictionsBySOId } from '../../../../../../hooks/entities/selectors/sales-orders';
import { useIsFedexHALBySalesOrderId } from '../../../../../../hooks/orders/use-fedex-hal-check';
import { useModal } from '../../../../../../hooks/use-modal';
import { useSalesOrderIdContext } from '../../../../../context/sales-order-id-context-provider';
import { SplitShipModal } from '../../../../../split-ship';
import { disableForItemCountOneOrLess } from '../../../../../split-ship/disable-split-ship';
import { useCombineSplitOrdersShipmentsFeatureGate } from '../../../../../../feature-gates/use-combine-split-orders-shipments-feature-gate';

import { launchDisabledMessages } from '../../../../../split-ship/messages';
import messages from '../messages';

import style from '../shipment-items.module.less';
import buttonTextStyle from 'ui-components/components/button/button-text.module.less';

const getDisabledInfo = ({ featureGate, hasOneOrLessItems, isFedexHalShipment }) => {
  let isDisabled = false;
  let disabledReasonMessageDescriptor;

  if (!featureGate.isFeatureEnabled) {
    isDisabled = true;
    disabledReasonMessageDescriptor = featureGate.disabledMessage;
  } else if (isFedexHalShipment) {
    isDisabled = true;
    disabledReasonMessageDescriptor = launchDisabledMessages.cantSplitHalOrder;
  } else if (hasOneOrLessItems) {
    isDisabled = true;
    disabledReasonMessageDescriptor = launchDisabledMessages.splitShipNotAllowedForOneItemOrders;
  }

  return {
    isDisabled,
    disabledReasonMessageDescriptor,
  };
};

const SplitShipButton = () => {
  const { isOpen, open, close } = useModal();
  const salesOrderId = useSalesOrderIdContext();
  const fulfillmentPlans = useEntitiesFulfillmentPlansFromSalesOrderId(salesOrderId);

  const salesOrderRestrictions = useSalesOrderRestrictionsBySOId(salesOrderId);
  const isFedexHalShipment = useIsFedexHALBySalesOrderId(salesOrderId);
  const hasOneOrLessItems = disableForItemCountOneOrLess(fulfillmentPlans);
  const featureGate = useCombineSplitOrdersShipmentsFeatureGate();

  const isSplitShipRestricted = salesOrderRestrictions?.splitShip?.componentState === ComponentStates.Disabled;
  if (isSplitShipRestricted) {
    return null;
  }

  const { isDisabled, disabledReasonMessageDescriptor } = getDisabledInfo({
    featureGate,
    hasOneOrLessItems,
    isFedexHalShipment,
  });

  const disabledTooltipMessage = disabledReasonMessageDescriptor ? <FormattedMessage {...disabledReasonMessageDescriptor} /> : undefined;

  return (
    <>
      <Button
        className={classnames(buttonTextStyle['button-text'], style['split-ship-button'])}
        disabled={isDisabled}
        disabledTooltipMessage={disabledTooltipMessage}
        onClick={open}
        theme='link'
      >
        <FormattedMessage
          {...messages.splitButton}
          values={{
            icon: <FontAwesomeIcon className={style.icon} icon={faSplit} />,
          }}
        />
      </Button>
      {isOpen && <SplitShipModal onClose={close} salesOrderId={salesOrderId} />}
    </>
  );
};

export default SplitShipButton;
